import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, LogOut, Search, Download, Upload, Database, ChevronLeft, ChevronRight, Archive, RotateCcw, History, Gem, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import { PHASES, PHASE_MAP } from "@/lib/phases";
import { SitePlaceholder } from "@/components/SitePlaceholder";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReasonDialog } from "@/components/ReasonDialog";

const empty = {
  name: "", slug: "", country: "", region: "", lat: 0, lng: 0,
  height_m: 0, original_height_m: 0, base_length_m: 0,
  era: "", estimated_age: "", stone_type: "", summary: "", mainstream: "",
  alt_names: "", gallery: "", theories: [],
};

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
    <input {...props} className="mt-1 w-full bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-accent" />
  </label>
);

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

// Fold diacritics so "Gobekli" matches "Göbekli", "Ggantija" matches "Ġgantija", etc.
const fold = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // slug being edited, or "new"
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [stubs, setStubs] = useState("hide"); // "hide" = curated only, "show" = full catalogue
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [restoring, setRestoring] = useState(false);
  const fileRef = useRef(null);
  const [binOpen, setBinOpen] = useState(false);
  const [deleted, setDeleted] = useState([]);
  const [binLoading, setBinLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditQuery, setAuditQuery] = useState("");
  const [reasonCtx, setReasonCtx] = useState(null); // { title, description, run: (reason) => Promise }
  const [dateJob, setDateJob] = useState(null); // { running, processed, total, updated, done }
  const [undatedOpen, setUndatedOpen] = useState(false);
  const [undated, setUndated] = useState([]);
  const [undatedLoading, setUndatedLoading] = useState(false);
  const [drafting, setDrafting] = useState(false);

  const aiDraft = async () => {
    const name = window.prompt("Enter the site name — AI will draft all the details for you:");
    if (!name || !name.trim()) return;
    setDrafting(true);
    const tId = toast.loading(`Researching “${name.trim()}” with AI…`);
    try {
      const r = await api.post("/admin/ai-draft", { name: name.trim() });
      const d = r.data;
      if (d.duplicate_slug) {
        toast.error(`Already in the archive: “${d.duplicate_name}”`, {
          id: tId,
          description: "Duplicates aren't allowed.",
          action: { label: "Open", onClick: () => window.open(`/pyramid/${d.duplicate_slug}`, "_blank") },
        });
        return;
      }
      setForm({
        ...empty,
        ...d,
        slug: "",
        alt_names: Array.isArray(d.alt_names) ? d.alt_names.join(", ") : (d.alt_names || ""),
        gallery: Array.isArray(d.gallery) ? d.gallery.join("\n") : "",
        theories: Array.isArray(d.theories) ? d.theories : [],
      });
      setEditing("new");
      const imgNote = Array.isArray(d.gallery) && d.gallery.length ? ` · ${d.gallery.length} photo${d.gallery.length > 1 ? "s" : ""} found` : "";
      toast.success(`Draft ready — review, then Save to add${imgNote}`, { id: tId });
    } catch (e) {
      toast.error("Draft failed", { id: tId, description: formatApiError(e.response?.data?.detail) });
    } finally {
      setDrafting(false);
    }
  };

  const loadUndated = useCallback(() => {
    setUndatedLoading(true);
    api.get("/admin/undated").then((r) => setUndated(r.data)).catch(() => {}).finally(() => setUndatedLoading(false));
  }, []);
  const openUndated = () => { setUndatedOpen(true); loadUndated(); };

  const pollDateJob = useCallback(async () => {
    try {
      const r = await api.get("/admin/estimate-dates/status");
      setDateJob(r.data);
      return r.data;
    } catch { return null; }
  }, []);

  useEffect(() => {
    if (!user) return;
    pollDateJob();
    const id = setInterval(async () => {
      const s = await pollDateJob();
      if (s && !s.running) clearInterval(id);
    }, 4000);
    return () => clearInterval(id);
  }, [user, pollDateJob]);

  const startDateJob = async () => {
    if (!window.confirm("Run the AI date-estimation pass over all undated entries? This uses LLM credits and runs in the background.")) return;
    try {
      await api.post("/admin/estimate-dates");
      toast.success("AI date estimation started", { description: "Progress will update below." });
      pollDateJob();
      const id = setInterval(async () => {
        const s = await pollDateJob();
        if (s && !s.running) { clearInterval(id); toast.success("Date estimation complete", { description: `${s.updated} entries dated` }); }
      }, 4000);
    } catch (e) {
      toast.error("Could not start", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const openAudit = () => {
    setAuditOpen(true);
    setAuditLoading(true);
    api.get("/admin/audit").then((r) => setAudit(r.data)).catch(() => {}).finally(() => setAuditLoading(false));
  };

  const loadDeleted = useCallback(() => {
    setBinLoading(true);
    api.get("/admin/deleted").then((r) => setDeleted(r.data)).catch(() => {}).finally(() => setBinLoading(false));
  }, []);

  const load = useCallback(() => {
    api.get("/pyramids", { params: { limit: 5000, stubs } }).then((r) => setItems(r.data)).catch(() => {});
  }, [stubs]);

  const displayed = useMemo(() => {
    let list = items;
    if (phaseFilter !== "all") list = list.filter((p) => p.phase === phaseFilter);
    if (sourceFilter !== "all") list = list.filter((p) => p.source === sourceFilter);
    if (q) {
      const s = fold(q);
      list = list.filter((p) => fold(p.name).includes(s) || fold(p.country).includes(s));
    }
    const sorters = {
      name: (a, b) => fold(a.name).localeCompare(fold(b.name)),
      "name-desc": (a, b) => fold(b.name).localeCompare(fold(a.name)),
      country: (a, b) => fold(a.country).localeCompare(fold(b.country)) || fold(a.name).localeCompare(fold(b.name)),
      phase: (a, b) => (a.phase || "").localeCompare(b.phase || "") || fold(a.name).localeCompare(fold(b.name)),
      height: (a, b) => (b.height_m || 0) - (a.height_m || 0),
    };
    return [...list].sort(sorters[sortBy] || sorters.name);
  }, [items, phaseFilter, sourceFilter, q, sortBy]);

  const totalPages = pageSize === "all" ? 1 : Math.max(1, Math.ceil(displayed.length / pageSize));
  const pageItems = pageSize === "all" ? displayed : displayed.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setPage(1); }, [q, phaseFilter, sourceFilter, sortBy, stubs, pageSize]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  useEffect(() => { if (user) load(); }, [user, load]);
  useEffect(() => { if (user) loadDeleted(); }, [user, loadDeleted]);
  useEffect(() => { if (user) loadUndated(); }, [user, loadUndated]);

  const openBin = () => { setBinOpen(true); loadDeleted(); };

  const restoreDeleted = async (d) => {
    try {
      await api.post(`/admin/deleted/${d.slug}/restore`);
      toast.success("Entry restored", { description: d.name });
      loadDeleted();
      load();
    } catch (e) {
      toast.error("Restore failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const purgeDeleted = async (d) => {
    if (!window.confirm(`Permanently delete "${d.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/deleted/${d.slug}`);
      toast.success("Permanently deleted", { description: d.name });
      loadDeleted();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const emptyBin = async () => {
    if (!window.confirm(`Permanently delete all ${deleted.length} entries in the Recycle Bin? This cannot be undone.`)) return;
    try {
      const res = await api.post("/admin/deleted/purge-all");
      toast.success(`Recycle Bin emptied`, { description: `${res.data.purged} entries permanently deleted` });
      loadDeleted();
    } catch (e) {
      toast.error("Empty bin failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const toggleSelect = (slug) => setSelected((s) => {
    const n = new Set(s);
    n.has(slug) ? n.delete(slug) : n.add(slug);
    return n;
  });
  const pageSlugs = pageItems.map((p) => p.slug);
  const allPageSelected = pageSlugs.length > 0 && pageSlugs.every((s) => selected.has(s));
  const toggleSelectPage = () => setSelected((s) => {
    const n = new Set(s);
    if (allPageSelected) pageSlugs.forEach((sl) => n.delete(sl));
    else pageSlugs.forEach((sl) => n.add(sl));
    return n;
  });
  const clearSelected = () => setSelected(new Set());

  const bulkDelete = () => {
    const slugs = [...selected];
    if (!slugs.length) return;
    setReasonCtx({
      title: `Remove ${slugs.length} selected ${slugs.length === 1 ? "entry" : "entries"}?`,
      description: "Pick a reason (applies to all selected). They move to the Recycle Bin.",
      run: async (reason) => {
        try {
          const res = await api.post("/pyramids/bulk-delete", { slugs, reason });
          toast.success(`${res.data.deleted} moved to Recycle Bin`);
          clearSelected();
          load();
          loadDeleted();
        } catch (e) {
          toast.error("Bulk delete failed", { description: formatApiError(e.response?.data?.detail) });
        }
      },
    });
  };

  if (loading) return <div className="px-6 py-32 text-center font-mono text-sm text-muted-foreground">Checking access…</div>;
  if (!user) return <Login />;

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (p) => {
    setForm({
      ...p,
      alt_names: (p.alt_names || []).join(", "),
      gallery: (p.gallery || []).join("\n"),
      theories: p.theories || [],
    });
    setEditing(p.slug);
  };
  const close = () => setEditing(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setTheory = (i, k, v) => setForm((f) => {
    const t = [...f.theories];
    t[i] = { ...t[i], [k]: v };
    return { ...f, theories: t };
  });
  const addTheory = () => setForm((f) => ({ ...f, theories: [...f.theories, { category: "", title: "", text: "" }] }));
  const removeTheory = (i) => setForm((f) => ({ ...f, theories: f.theories.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      slug: form.slug || slugify(form.name),
      lat: parseFloat(form.lat) || 0,
      lng: parseFloat(form.lng) || 0,
      height_m: parseFloat(form.height_m) || 0,
      original_height_m: parseFloat(form.original_height_m) || 0,
      base_length_m: parseFloat(form.base_length_m) || 0,
      alt_names: form.alt_names ? form.alt_names.split(",").map((s) => s.trim()).filter(Boolean) : [],
      gallery: form.gallery ? form.gallery.split("\n").map((s) => s.trim()).filter(Boolean) : [],
      theories: form.theories.filter((t) => t.title || t.text),
    };
    try {
      if (editing === "new") await api.post("/pyramids", payload);
      else await api.put(`/pyramids/${editing}`, payload);
      toast.success(editing === "new" ? "Entry created" : "Entry updated", { description: payload.name });
      close();
      load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const del = (p) => {
    setReasonCtx({
      title: `Remove “${p.name}”?`,
      description: "Pick a reason. It moves to the Recycle Bin and can be restored later.",
      run: async (reason) => {
        try {
          await api.delete(`/pyramids/${p.slug}`, { params: { reason } });
          toast.success("Moved to Recycle Bin", { description: p.name });
          load();
          loadDeleted();
        } catch (e) {
          toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) });
        }
      },
    });
  };

  const downloadBackup = async () => {
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers["content-disposition"] || "";
      const m = disp.match(/filename="(.+?)"/);
      a.download = m ? m[1] : "anomalous-earth-backup.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error("Backup failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const restoreBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm("Restore will REPLACE the entire database (entries, users, favorites) with the contents of this backup file. This cannot be undone. Continue?")) return;
    setRestoring(true);
    try {
      const text = await file.text();
      const res = await api.post("/admin/restore", text, { headers: { "Content-Type": "application/json" } });
      const s = res.data.restored || {};
      toast.success("Database restored", { description: Object.entries(s).map(([k, v]) => `${v} ${k}`).join(" · ") });
      load();
    } catch (e2) {
      toast.error("Restore failed", { description: formatApiError(e2.response?.data?.detail) });
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-8 lg:px-12 py-14">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-1">Catalogue Manager</p>
          <h1 className="font-serif text-4xl font-bold text-foreground">Admin Panel</h1>
          <p className="mt-1 text-sm text-muted-foreground">Signed in as {user.email}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="admin-audit-log-button" onClick={openAudit} className="flex items-center gap-2 border border-border font-mono text-xs uppercase tracking-widest px-4 py-3 hover:bg-secondary transition-colors">
            <History className="h-4 w-4" /> Audit Log
          </button>
          <button data-testid="admin-undated-button" onClick={openUndated} className="relative flex items-center gap-2 border border-border font-mono text-xs uppercase tracking-widest px-4 py-3 hover:bg-secondary transition-colors">
            <Gem className="h-4 w-4" /> Undated
            {undated.length > 0 && <span data-testid="admin-undated-count" className="ml-1 bg-accent text-accent-foreground rounded-full px-1.5 py-0.5 text-[10px] leading-none">{undated.length}</span>}
          </button>
          <button data-testid="admin-recycle-bin-button" onClick={openBin} className="relative flex items-center gap-2 border border-border font-mono text-xs uppercase tracking-widest px-4 py-3 hover:bg-secondary transition-colors">
            <Archive className="h-4 w-4" /> Recycle Bin
            {deleted.length > 0 && <span data-testid="admin-bin-count" className="ml-1 bg-accent text-accent-foreground rounded-full px-1.5 py-0.5 text-[10px] leading-none">{deleted.length}</span>}
          </button>
          <button data-testid="admin-ai-draft-button" onClick={aiDraft} disabled={drafting} className="flex items-center gap-2 border border-accent/50 text-accent font-mono text-xs uppercase tracking-widest px-4 py-3 hover:bg-accent/10 transition-colors disabled:opacity-50">
            <Sparkles className="h-4 w-4" /> {drafting ? "Drafting…" : "AI Add"}
          </button>
          <button data-testid="admin-add-pyramid-button" onClick={openNew} className="flex items-center gap-2 bg-accent text-accent-foreground font-mono text-xs uppercase tracking-widest px-5 py-3 hover:bg-accent/90 transition-colors">
            <Plus className="h-4 w-4" /> Add Entry
          </button>
          <button onClick={logout} className="flex items-center gap-2 border border-border font-mono text-xs uppercase tracking-widest px-4 py-3 hover:bg-secondary transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="admin-backup-panel">
        <div className="border border-border bg-card p-4 flex items-center gap-4">
          <Database className="h-5 w-5 text-accent shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-serif text-sm font-semibold text-foreground">Backup database</p>
            <p className="text-xs text-muted-foreground">Download entries, users &amp; favorites as JSON.</p>
          </div>
          <button data-testid="admin-backup-button" onClick={downloadBackup} className="flex items-center gap-2 border border-border font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 hover:bg-secondary transition-colors">
            <Download className="h-4 w-4" /> Backup
          </button>
        </div>
        <div className="border border-border bg-card p-4 flex items-center gap-4">
          <Upload className="h-5 w-5 text-accent shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-serif text-sm font-semibold text-foreground">Restore database</p>
            <p className="text-xs text-muted-foreground">Replaces everything with a backup file.</p>
          </div>
          <input ref={fileRef} type="file" accept="application/json,.json" onChange={restoreBackup} className="hidden" data-testid="admin-restore-input" />
          <button data-testid="admin-restore-button" disabled={restoring} onClick={() => fileRef.current?.click()} className="flex items-center gap-2 border border-destructive/50 text-destructive font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 hover:bg-destructive hover:text-destructive-foreground transition-colors disabled:opacity-50">
            <Upload className="h-4 w-4" /> {restoring ? "Restoring…" : "Restore"}
          </button>
        </div>
      </div>

      <div className="mt-3 border border-border bg-card p-4 flex items-center gap-4" data-testid="admin-ai-dates-panel">
        <History className="h-5 w-5 text-accent shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-serif text-sm font-semibold text-foreground">Estimate dates with AI</p>
          <p className="text-xs text-muted-foreground">
            Fills an approximate era/date for undated entries so they appear on the Timeline.
            {dateJob && (dateJob.running || dateJob.done) && (
              <span className="ml-1 font-mono text-[11px] text-foreground">
                {dateJob.running ? `Running… ${dateJob.processed || 0}/${dateJob.total || 0} checked, ${dateJob.updated || 0} dated` : `Done · ${dateJob.updated || 0} dated`}
              </span>
            )}
          </p>
        </div>
        <button
          data-testid="admin-estimate-dates-button"
          onClick={startDateJob}
          disabled={dateJob?.running}
          className="flex items-center gap-2 border border-border font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 hover:bg-secondary transition-colors disabled:opacity-50"
        >
          <History className="h-4 w-4" /> {dateJob?.running ? "Running…" : "Estimate Dates"}
        </button>
      </div>


      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="admin-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or country…"
            className="w-full bg-card border border-border pl-10 pr-4 py-2.5 font-mono text-sm focus:outline-none focus:border-accent"
          />
        </div>
        <div className="w-40">
          <Select value={phaseFilter} onValueChange={setPhaseFilter}>
            <SelectTrigger data-testid="admin-phase-filter" className="font-mono text-xs uppercase tracking-wider"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Phases</SelectItem>
              {PHASES.map((ph) => <SelectItem key={ph.key} value={ph.key}>{ph.roman} · {ph.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-36">
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger data-testid="admin-source-filter" className="font-mono text-xs uppercase tracking-wider"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="curated">Curated</SelectItem>
              <SelectItem value="wikidata">Imported</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="admin-sort" className="font-mono text-xs uppercase tracking-wider"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Sort: Name A–Z</SelectItem>
              <SelectItem value="name-desc">Sort: Name Z–A</SelectItem>
              <SelectItem value="country">Sort: Country</SelectItem>
              <SelectItem value="phase">Sort: Phase</SelectItem>
              <SelectItem value="height">Sort: Height</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Select value={stubs} onValueChange={setStubs}>
            <SelectTrigger data-testid="admin-catalogue-toggle" className="font-mono text-xs uppercase tracking-wider"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="hide">Curated only</SelectItem>
              <SelectItem value="show">Full catalogue</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-32">
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(v === "all" ? "all" : Number(v))}>
            <SelectTrigger data-testid="admin-page-size" className="font-mono text-xs uppercase tracking-wider"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="50">50 / page</SelectItem>
              <SelectItem value="100">100 / page</SelectItem>
              <SelectItem value="all">Show all</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground cursor-pointer select-none">
          <input type="checkbox" data-testid="admin-select-page" checked={allPageSelected} onChange={toggleSelectPage} className="h-4 w-4 accent-[var(--accent)]" />
          Select page
        </label>
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground" data-testid="admin-count">
          Showing {pageItems.length} of {displayed.length} entries
          {pageSize !== "all" && totalPages > 1 ? ` · page ${page} / ${totalPages}` : ""}
        </p>
        {selected.size > 0 && (
          <div className="flex items-center gap-2 ml-auto" data-testid="admin-bulk-bar">
            <span className="font-mono text-[11px] uppercase tracking-widest text-foreground">{selected.size} selected</span>
            <button data-testid="admin-bulk-delete" onClick={bulkDelete} className="flex items-center gap-1.5 border border-destructive/50 text-destructive font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:bg-destructive hover:text-destructive-foreground transition-colors">
              <Trash2 className="h-3.5 w-3.5" /> Delete selected
            </button>
            <button data-testid="admin-bulk-clear" onClick={clearSelected} className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground px-2 py-2">Clear</button>
          </div>
        )}
      </div>

      <div className="mt-3 border border-border divide-y divide-border" data-testid="admin-table">
        {pageItems.map((p) => {
          const ph = PHASE_MAP[p.phase] || PHASE_MAP.pyramids;
          return (
          <div key={p.slug} className={`flex items-center gap-4 p-4 transition-colors ${selected.has(p.slug) ? "bg-accent/10" : "hover:bg-secondary/40"}`}>
            <input type="checkbox" data-testid={`admin-select-${p.slug}`} checked={selected.has(p.slug)} onChange={() => toggleSelect(p.slug)} className="h-4 w-4 shrink-0 accent-[var(--accent)]" />
            {p.gallery?.[0] ? (
              <img src={p.gallery[0]} alt="" className="h-12 w-16 object-cover border border-border shrink-0" />
            ) : (
              <SitePlaceholder phase={p.phase} compact className="h-12 w-16 border border-border shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg font-semibold text-foreground truncate">{p.name}</p>
              <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                <span style={{ color: ph.color }}>{ph.roman}</span> · {p.country} · {p.lat.toFixed(2)}, {p.lng.toFixed(2)}
              </p>
            </div>
            <button data-testid={`admin-edit-${p.slug}`} onClick={() => openEdit(p)} className="p-2 border border-border hover:bg-background transition-colors" title="Edit">
              <Pencil className="h-4 w-4" />
            </button>
            <button data-testid={`admin-delete-${p.slug}`} onClick={() => del(p)} className="p-2 border border-border hover:bg-destructive hover:text-destructive-foreground transition-colors" title="Delete">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        );})}
      </div>

      {pageSize !== "all" && totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3" data-testid="admin-pagination">
          <button data-testid="admin-page-prev" disabled={page <= 1} onClick={() => setPage((n) => Math.max(1, n - 1))} className="flex items-center gap-1 border border-border font-mono text-[11px] uppercase tracking-widest px-3 py-2 hover:bg-secondary transition-colors disabled:opacity-40">
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Page {page} / {totalPages}</span>
          <button data-testid="admin-page-next" disabled={page >= totalPages} onClick={() => setPage((n) => Math.min(totalPages, n + 1))} className="flex items-center gap-1 border border-border font-mono text-[11px] uppercase tracking-widest px-3 py-2 hover:bg-secondary transition-colors disabled:opacity-40">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <ReasonDialog
        open={!!reasonCtx}
        onOpenChange={(v) => { if (!v) setReasonCtx(null); }}
        title={reasonCtx?.title || ""}
        description={reasonCtx?.description || ""}
        onConfirm={(reason) => { const ctx = reasonCtx; setReasonCtx(null); ctx?.run(reason); }}
      />

      {/* Undated explorer drawer */}
      {undatedOpen && (
        <div className="fixed inset-0 z-[60] flex justify-end bg-black/50" onClick={() => setUndatedOpen(false)}>
          <div className="w-full max-w-xl h-full overflow-y-auto bg-card border-l border-border p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-undated-drawer">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-serif text-2xl font-bold text-foreground flex items-center gap-2"><Gem className="h-5 w-5 text-accent" /> Undated Sites</h2>
              <button onClick={() => setUndatedOpen(false)} className="p-2 hover:bg-secondary"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              {undated.length} entries still have no date and don't appear on the Timeline. Open one and run its
              deep-dive to give it a sourced date.
            </p>
            {undatedLoading ? (
              <p className="font-mono text-xs text-muted-foreground">Loading…</p>
            ) : undated.length === 0 ? (
              <p data-testid="admin-undated-empty" className="font-mono text-xs uppercase tracking-widest text-muted-foreground py-8 text-center">Every entry has a date. 🎉</p>
            ) : (
              <div className="border border-border divide-y divide-border">
                {undated.map((u) => (
                  <a
                    key={u.slug}
                    href={`/pyramid/${u.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`undated-row-${u.slug}`}
                    className="p-4 flex items-center gap-3 hover:bg-secondary/40 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-base font-semibold text-foreground truncate">{u.name}</p>
                      <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground truncate">{u.country || "—"} · {u.phase}</p>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-accent shrink-0">Open →</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit log drawer */}
      {auditOpen && (
        <div className="fixed inset-0 z-[60] flex justify-end bg-black/50" onClick={() => setAuditOpen(false)}>
          <div className="w-full max-w-2xl h-full overflow-y-auto bg-card border-l border-border p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-audit-log">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-serif text-2xl font-bold text-foreground flex items-center gap-2"><History className="h-5 w-5 text-accent" /> Audit Log</h2>
              <button onClick={() => setAuditOpen(false)} className="p-2 hover:bg-secondary"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Recent sign-ins, sign-ups, logouts and failed attempts, with the IP address they connected from.</p>

            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                data-testid="audit-search"
                value={auditQuery}
                onChange={(e) => setAuditQuery(e.target.value)}
                placeholder="Filter by email or IP…"
                className="w-full bg-background border border-border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-accent"
              />
            </div>

            {(() => {
              const eventStyle = (ev) => {
                if (ev === "register") return "bg-accent/15 text-accent";
                if (ev === "login_failed" || ev === "login_blocked") return "bg-destructive/15 text-destructive";
                if (ev === "logout") return "bg-secondary text-foreground";
                return "bg-secondary text-muted-foreground";
              };
              const term = auditQuery.trim().toLowerCase();
              const visible = term ? audit.filter((a) => (a.email || "").toLowerCase().includes(term) || (a.ip || "").toLowerCase().includes(term)) : audit;
              if (auditLoading) return <p className="font-mono text-xs text-muted-foreground">Loading…</p>;
              if (audit.length === 0) return <p data-testid="admin-audit-empty" className="font-mono text-xs uppercase tracking-widest text-muted-foreground py-8 text-center">No activity recorded yet.</p>;
              if (visible.length === 0) return <p data-testid="admin-audit-no-match" className="font-mono text-xs uppercase tracking-widest text-muted-foreground py-8 text-center">No entries match “{auditQuery}”.</p>;
              return (
                <div className="border border-border divide-y divide-border">
                  {visible.map((a, i) => (
                    <div key={i} className="p-4 flex items-center gap-4" data-testid="audit-row">
                      <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 shrink-0 ${eventStyle(a.event)}`}>{a.event}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-base font-semibold text-foreground truncate">{a.email} <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">· {a.role}</span></p>
                        <p className="font-mono text-[11px] text-muted-foreground truncate" title={a.user_agent}>{a.user_agent || "—"}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-mono text-xs text-foreground" data-testid="audit-ip">{a.ip || "—"}</p>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{a.at ? new Date(a.at).toLocaleString() : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Recycle bin drawer */}
      {binOpen && (
        <div className="fixed inset-0 z-[60] flex justify-end bg-black/50" onClick={() => setBinOpen(false)}>
          <div className="w-full max-w-xl h-full overflow-y-auto bg-card border-l border-border p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-recycle-bin">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-serif text-2xl font-bold text-foreground flex items-center gap-2"><Archive className="h-5 w-5 text-accent" /> Recycle Bin</h2>
              <div className="flex items-center gap-2">
                {deleted.length > 0 && (
                  <button data-testid="admin-empty-bin" onClick={emptyBin} className="flex items-center gap-1.5 border border-destructive/50 text-destructive font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:bg-destructive hover:text-destructive-foreground transition-colors">
                    <Trash2 className="h-3.5 w-3.5" /> Empty bin
                  </button>
                )}
                <button onClick={() => setBinOpen(false)} className="p-2 hover:bg-secondary"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-5">Deleted entries are kept here. Restore brings them back to the catalogue; permanent delete cannot be undone.</p>

            {binLoading ? (
              <p className="font-mono text-xs text-muted-foreground">Loading…</p>
            ) : deleted.length === 0 ? (
              <p data-testid="admin-bin-empty" className="font-mono text-xs uppercase tracking-widest text-muted-foreground py-8 text-center">The recycle bin is empty.</p>
            ) : (
              <div className="border border-border divide-y divide-border">
                {deleted.map((d) => (
                  <div key={d.slug} className="p-4 flex items-center gap-3" data-testid={`bin-row-${d.slug}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-base font-semibold text-foreground truncate">{d.name}</p>
                      <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground truncate">
                        {d.country || "—"} · {d.source || "?"} · by {d.deleted_by || "?"}
                        {d.deleted_at ? ` · ${new Date(d.deleted_at).toLocaleDateString()}` : ""}
                      </p>
                      {d.reason ? <p className="mt-0.5 font-serif text-xs italic text-accent truncate" title={d.reason}>“{d.reason}”</p> : null}
                    </div>
                    <button data-testid={`bin-restore-${d.slug}`} onClick={() => restoreDeleted(d)} title="Restore" className="flex items-center gap-1.5 border border-border font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:bg-secondary transition-colors">
                      <RotateCcw className="h-3.5 w-3.5" /> Restore
                    </button>
                    <button data-testid={`bin-purge-${d.slug}`} onClick={() => purgeDeleted(d)} title="Delete permanently" className="p-2 border border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-[60] flex justify-end bg-black/50" onClick={close}>
          <div className="w-full max-w-2xl h-full overflow-y-auto bg-card border-l border-border p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-editor">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl font-bold text-foreground">{editing === "new" ? "New Entry" : "Edit Entry"}</h2>
              <button onClick={close} className="p-2 hover:bg-secondary"><X className="h-5 w-5" /></button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Name *" data-testid="form-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                <Field label="Slug (auto)" data-testid="form-slug" value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto from name" disabled={editing !== "new"} />
                <Field label="Country *" data-testid="form-country" value={form.country} onChange={(e) => set("country", e.target.value)} />
                <Field label="Region" value={form.region} onChange={(e) => set("region", e.target.value)} />
                <Field label="Latitude *" type="number" step="any" data-testid="form-lat" value={form.lat} onChange={(e) => set("lat", e.target.value)} />
                <Field label="Longitude *" type="number" step="any" data-testid="form-lng" value={form.lng} onChange={(e) => set("lng", e.target.value)} />
                <Field label="Height (m)" type="number" step="any" value={form.height_m} onChange={(e) => set("height_m", e.target.value)} />
                <Field label="Original Height (m)" type="number" step="any" value={form.original_height_m} onChange={(e) => set("original_height_m", e.target.value)} />
                <Field label="Base Length (m)" type="number" step="any" value={form.base_length_m} onChange={(e) => set("base_length_m", e.target.value)} />
                <Field label="Era" value={form.era} onChange={(e) => set("era", e.target.value)} />
                <Field label="Estimated Age" value={form.estimated_age} onChange={(e) => set("estimated_age", e.target.value)} />
                <Field label="Stone / Construction" value={form.stone_type} onChange={(e) => set("stone_type", e.target.value)} />
              </div>

              <Field label="Alternative Names (comma-separated)" value={form.alt_names} onChange={(e) => set("alt_names", e.target.value)} />

              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Summary</span>
                <textarea value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={2} className="mt-1 w-full bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-accent" />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Mainstream Explanation</span>
                <textarea value={form.mainstream} onChange={(e) => set("mainstream", e.target.value)} rows={3} className="mt-1 w-full bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-accent" />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Gallery Image URLs (one per line)</span>
                <textarea data-testid="form-gallery" value={form.gallery} onChange={(e) => set("gallery", e.target.value)} rows={3} className="mt-1 w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-accent" />
              </label>

              {/* Theories */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Mysteries &amp; Theories</span>
                  <button onClick={addTheory} className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-accent"><Plus className="h-3 w-3" /> Add</button>
                </div>
                <div className="space-y-3">
                  {form.theories.map((t, i) => (
                    <div key={i} className="border border-border p-3 space-y-2 relative">
                      <button onClick={() => removeTheory(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
                      <input value={t.category} onChange={(e) => setTheory(i, "category", e.target.value)} placeholder="Category" className="w-full bg-background border border-border px-3 py-1.5 text-sm focus:outline-none focus:border-accent" />
                      <input value={t.title} onChange={(e) => setTheory(i, "title", e.target.value)} placeholder="Title" className="w-full bg-background border border-border px-3 py-1.5 text-sm focus:outline-none focus:border-accent" />
                      <textarea value={t.text} onChange={(e) => setTheory(i, "text", e.target.value)} placeholder="Detail" rows={2} className="w-full bg-background border border-border px-3 py-1.5 text-sm focus:outline-none focus:border-accent" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button data-testid="admin-save-pyramid-button" onClick={save} disabled={saving || !form.name || !form.country} className="flex-1 bg-accent text-accent-foreground font-mono text-xs uppercase tracking-widest py-3.5 hover:bg-accent/90 transition-colors disabled:opacity-50">
                  {saving ? "Saving…" : "Save Entry"}
                </button>
                <button onClick={close} className="px-6 border border-border font-mono text-xs uppercase tracking-widest hover:bg-secondary transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
